import { alpha, Theme } from "@mui/material/styles";
import color from "color";
import { GT } from "./appcolors";

/**
 * Button semantics, in one place.
 *
 * The portal had buttons in blue, emerald, indigo, two different greys and a
 * dark navy, chosen per page, and none of them agreed on what a colour *meant*.
 * The rule this file encodes is that colour is decided by what the button does,
 * never by which screen it happens to sit on:
 *
 *   primary   the page's main action        Add Client, Save, Send, Confirm
 *   success   a positive utility action     Export, Download, Generate report
 *   neutral   a supporting action           Setting, Reset, Cancel, Back, Today
 *   danger    destructive                   Delete, Remove, Void
 *   warning   needs care but not destructive Suspend, Pending
 *
 * Every value resolves through the MUI palette rather than a literal hex, so an
 * installation that reconfigures its scheme from Setting → Theme Color still
 * drives all of them. That constraint is why there are no colour constants in
 * this file at all.
 *
 * Consumed by three layers, so they cannot drift apart:
 *   theme/components.ts   the global MUI + FullCalendar button appearance
 *   theme/appStyles.ts    the legacy `classes.addNewButton` family
 *   Components/Design/ActionButton.tsx  the design-system button
 */

export type ButtonRole =
  | "primary"
  | "success"
  | "neutral"
  | "danger"
  | "warning";

export interface ButtonRoleStyle {
  /** Resting fill. */
  background: string;
  /** Resting ink — always ≥ 4.5:1 against `background`. */
  foreground: string;
  border: string;
  hoverBackground: string;
  hoverForeground: string;
  hoverBorder: string;
}

/**
 * Is this colour too close to grey to read as an action?
 *
 * This exists because of a real configuration in the field: the theme record
 * ships `addButtonColor: "#7a7f87"`, and every "Add New" in the product
 * therefore rendered as a dull slate block that users read as disabled. The
 * colour is not wrong as *data* — it is a legitimate saved value — so it is not
 * overwritten anywhere. It is simply not usable as a primary action, and a
 * primary action that looks switched off is a defect no matter which layer
 * chose the colour.
 *
 * The same "the configured value defeats the design, so fall back" pattern is
 * already used by `moduleAccent.ts` when an installation sets `secondary` to
 * the same value as `primary`. A genuinely chromatic choice — a brand green, a
 * deep purple — still wins, so the setting keeps working for anyone who means
 * it.
 */
export const isDullNeutral = (value: string): boolean => {
  try {
    const parsed = color(value);
    /* Saturation is the honest test here: #7a7f87 is 5% saturated, while any
       deliberate brand colour in this palette is well above 30%. Near-black and
       near-white are caught too — both read as chrome rather than as an action. */
    return parsed.saturationl() < 18 || parsed.lightness() > 92;
  } catch {
    /* Unparseable means we cannot vouch for it; the brand colour is the safe
       answer for a primary action. */
    return true;
  }
};

/**
 * The colour a primary action should actually use.
 *
 * Three candidates, in order of how much the installation asked for them:
 * the configured add-button colour, then the configured primary, then the GT
 * brand default. The first one that can actually carry a filled action wins.
 *
 * The second step matters in dark mode, where this installation's saved theme
 * sets `primary: "#9d939f"` — a desaturated mauve that makes every "Add New" in
 * the product read as a disabled grey block, exactly as `#7a7f87` does in light
 * mode.
 *
 * The fallback is deliberately scoped to *button fills* — it is reached only
 * through `buttonRole`. `theme.palette.primary` still drives links, focus rings,
 * the active nav marker and everything else, so an installation's choice is
 * overridden in one place and only where it would otherwise produce a control
 * that looks switched off. Nothing is saved, migrated or written back.
 */
export const primaryActionColor = (theme: Theme): string => {
  const configured = theme.palette.iconButton?.addButtonColor;
  if (configured && !isDullNeutral(configured)) return configured;

  const brand = theme.palette.primary.main;
  if (brand && !isDullNeutral(brand)) return brand;

  return theme.palette.mode === "light" ? GT.blue : GT.cyan;
};

/** Readable ink for a solid fill of `background`. */
const inkOn = (theme: Theme, background: string): string => {
  try {
    return theme.palette.getContrastText(background);
  } catch {
    return "#ffffff";
  }
};

/** One step deeper, for a solid button's hover. */
const deepen = (value: string, isLight: boolean): string => {
  try {
    return isLight ? color(value).darken(0.16).hex() : color(value).lighten(0.12).hex();
  } catch {
    return value;
  }
};

/**
 * The resting and hover appearance for a role.
 *
 * `solid` is the emphatic treatment — a filled surface, used for the single
 * dominant action on a screen. Everything else is the tinted treatment: a wash
 * of the family at 7% with a 28% border, which is the appearance the Policy
 * Report's "Export to Excel" button already had and which this standardises on.
 */
export const buttonRole = (
  theme: Theme,
  role: ButtonRole,
  solid = false
): ButtonRoleStyle => {
  const isLight = theme.palette.mode === "light";

  if (role === "neutral") {
    /* Not a colour family: a real surface with a border. The old treatment was
       a heavy grey block, which is why Setting and Today read as disabled. */
    const brand = theme.palette.primary.main;
    return {
      background: isLight
        ? theme.palette.cardAdd.bgColor
        : alpha(theme.palette.common.white, 0.03),
      foreground: theme.palette.text.primary,
      border: alpha(theme.palette.divider, isLight ? 0.9 : 0.6),
      hoverBackground: alpha(brand, isLight ? 0.08 : 0.14),
      hoverForeground: isLight
        ? theme.palette.primary.dark || brand
        : theme.palette.primary.light || brand,
      hoverBorder: alpha(brand, isLight ? 0.42 : 0.5),
    };
  }

  const family =
    role === "primary"
      ? { main: primaryActionColor(theme), slot: theme.palette.primary }
      : role === "success"
      ? { main: theme.palette.success.main, slot: theme.palette.success }
      : role === "danger"
      ? { main: theme.palette.error.main, slot: theme.palette.error }
      : { main: theme.palette.warning.main, slot: theme.palette.warning };

  const accent = family.main;
  /* Dark surfaces need the lighter end of the family and light surfaces the
     darker end, or an amber "warning" renders as unreadable yellow on white. */
  const ink = (isLight ? family.slot.dark : family.slot.light) || accent;

  if (solid) {
    return {
      background: accent,
      foreground: inkOn(theme, accent),
      border: accent,
      hoverBackground: deepen(accent, isLight),
      hoverForeground: inkOn(theme, accent),
      hoverBorder: deepen(accent, isLight),
    };
  }

  return {
    background: alpha(accent, isLight ? 0.07 : 0.14),
    foreground: ink,
    border: alpha(accent, isLight ? 0.28 : 0.38),
    hoverBackground: alpha(accent, isLight ? 0.15 : 0.24),
    hoverForeground: ink,
    hoverBorder: alpha(accent, isLight ? 0.45 : 0.55),
  };
};

/**
 * The same appearance as a plain style object, for the `makeStyles` classes in
 * `appStyles.ts` that cannot call a hook.
 */
export const buttonRoleSx = (
  theme: Theme,
  role: ButtonRole,
  solid = false
) => {
  const s = buttonRole(theme, role, solid);
  return {
    backgroundColor: s.background,
    color: s.foreground,
    border: `1px solid ${s.border}`,
    "&:hover": {
      backgroundColor: s.hoverBackground,
      color: s.hoverForeground,
      borderColor: s.hoverBorder,
      boxShadow: "none",
    },
  };
};
